import { HttpErrorResponse } from '@angular/common/http';
import { OperatorFunction, catchError, of } from 'rxjs';
import { APIResponse } from '../data-access/api-response.model';

export const NETWORK_ERROR_MSG = 'Unable to reach the server. Please check your connection and try again.';
export const FORBIDDEN_ERROR_MSG = 'You do not have permission to do that.';
export const GENERIC_ERROR_MSG = 'Something went wrong. Please try again.';

/**
 * Turns a failed HTTP call into the same `{ success: false, msg }` shape the backend uses for
 * handled errors, so components can keep using their `if (response.success) ... else ...` branch.
 */
export function toApiErrorResponse(error: unknown): APIResponse {
  return {
    success: false,
    msg: getErrorMessage(error),
    result: null,
    total: null,
    page: null,
    limit: null,
    total_pages: null,
    items: null,
    message: null,
    totalcount: 0,
  };
}

function getErrorMessage(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) {
    return GENERIC_ERROR_MSG;
  }
  if (error.status === 0) {
    return NETWORK_ERROR_MSG;
  }
  if (error.status === 403) {
    return FORBIDDEN_ERROR_MSG;
  }
  const bodyMsg = error.error?.msg;
  return typeof bodyMsg === 'string' && bodyMsg.length > 0 ? bodyMsg : GENERIC_ERROR_MSG;
}

/**
 * Use in place of `catchError(() => [])`, which completes the stream without emitting and leaves
 * subscribers (and their loading spinners) waiting forever.
 */
export const catchApiError = <T>(): OperatorFunction<T, T | APIResponse> =>
  catchError((error: unknown) => {
    console.error(error);
    return of(toApiErrorResponse(error));
  });

/**
 * For methods that map the response into a plain value (e.g. `boolean`) before catching: log the
 * error and emit `fallback` so the stream still produces a value the caller can act on.
 */
export const catchErrorWith = <T, F>(fallback: F): OperatorFunction<T, T | F> =>
  catchError((error: unknown) => {
    console.error(error);
    return of(fallback);
  });
